import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  alpha,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";

const LANGUAGES = [
  { code: "en", labelKey: "languages.english" },
  { code: "hi", labelKey: "languages.hindi" },
  { code: "mr", labelKey: "languages.marathi" },
];

const SUPPORTED_LANGS = ["en", "hi", "mr"];

function getCurrentLang(lng) {
  if (!lng) return "en";
  const base = lng.split("-")[0];
  return SUPPORTED_LANGS.includes(base) ? base : "en";
}

export default function LanguageDropdown() {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const currentLang = getCurrentLang(i18n.language);
  const currentLabel = t(LANGUAGES.find((l) => l.code === currentLang)?.labelKey || "languages.english");

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    handleClose();
  };

  return (
    <>
      <Button
        id="language-dropdown-button"
        aria-controls={open ? "language-menu" : undefined}
        aria-hovered={open ? "true" : undefined}
        aria-expanded={open ? "true" : undefined}
        onClick={handleOpen}
        disableRipple
        startIcon={
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LanguageIcon sx={{ color: "white", fontSize: "1.1rem" }} />
          </Box>
        }
        endIcon={
          <ExpandMoreIcon
            sx={{
              color: "white",
              fontSize: "1.25rem",
              transition: "transform 0.2s ease",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        }
        sx={{
          color: "white",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.9rem",
          letterSpacing: "0.02em",
          px: 1.5,
          py: 0.75,
          borderRadius: "12px",
          background: "rgba(255,255,255,0.12)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.2)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          "&:hover": {
            background: "rgba(255,255,255,0.22)",
            borderColor: "rgba(255,255,255,0.35)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          },
          "& .MuiButton-startIcon": { mr: 1.25 },
          "& .MuiButton-endIcon": { ml: 0.5 },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.2 }}>
          <Typography component="span" variant="caption" sx={{ opacity: 0.9, fontSize: "0.7rem" }}>
            {t("common.language")}
          </Typography>
          <Typography component="span" variant="body2" fontWeight={600}>
            {currentLabel}
          </Typography>
        </Box>
      </Button>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "language-dropdown-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            border: "1px solid rgba(0,0,0,0.06)",
            "& .MuiMenuItem-root": {
              py: 1.25,
              px: 2,
              gap: 1.5,
            },
            "& .MuiMenuItem-root:hover": {
              backgroundColor: alpha("#7A3FFF", 0.08),
            },
            "& .MuiMenuItem-root.Mui-selected": {
              backgroundColor: alpha("#7A3FFF", 0.12),
              "&:hover": {
                backgroundColor: alpha("#7A3FFF", 0.18),
              },
            },
          },
        }}
      >
        {LANGUAGES.map((lang) => {
          const isSelected = currentLang === lang.code;
          return (
            <MenuItem
              key={lang.code}
              selected={isSelected}
              onClick={() => handleSelect(lang.code)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    background: isSelected ? "linear-gradient(135deg, #7028DA 0%, #9E3ED7 100%)" : "#f0f0f0",
                    color: isSelected ? "white" : "#666",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                  }}
                >
                  {lang.code.toUpperCase()}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={t(lang.labelKey)}
                primaryTypographyProps={{
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? "#7028DA" : "text.primary",
                }}
              />
              {isSelected && (
                <CheckIcon sx={{ color: "#7028DA", fontSize: "1.2rem" }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
